import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { t } from '@/i18n';
import { toastApiError } from '@/queries/common';
import { addDamagePolygon } from '@/queries/damage-polygons/add-damage-polygon';
import { updateDamagePolygon } from '@/queries/damage-polygons/update-damage-polygon';
import { deleteDamagePolygon } from '@/queries/damage-polygons/delete-damage-polygon';
import type { DamagePolygonType } from '@/common/damage-polygon';

type UsePolygonMutationsProps = {
	assetId: string;
	inspectionId: string;
	damageId: string;
	// Callbacks for specific actions
	onCreateSuccess?: (data: DamagePolygonType) => void;
	onUpdateSuccess?: (data: DamagePolygonType) => void;
	onDeleteSuccess?: () => void;
	onLinkSuccess?: () => void;
	onUnlinkSuccess?: () => void;
};

export function usePolygonMutations({
	assetId,
	inspectionId,
	damageId,
	onCreateSuccess,
	onUpdateSuccess,
	onDeleteSuccess,
	onLinkSuccess,
	onUnlinkSuccess,
}: UsePolygonMutationsProps) {
	const queryClient = useQueryClient();

	// --- Shared Invalidation Logic ---
	const invalidateAll = async () => {
		// 1. Refresh any specific damage queries (to show "Linked" status)
		await queryClient.invalidateQueries({
			queryKey: ['damage'],
		});
		// 2. Refresh the table list
		await queryClient.invalidateQueries({ queryKey: ['damages'] });
		// 3. Refresh the 3D Map
		await queryClient.invalidateQueries({ queryKey: ['damage-polygons'] });
	};

	// --- CREATE ---
	const createMutation = useMutation({
		mutationFn: (
			data: Omit<
				DamagePolygonType,
				'id' | 'createdAt' | 'updatedAt' | 'createdById' | 'updatedById'
			>
		) => addDamagePolygon(data),
		onSuccess: async (newItem) => {
			toast.success(t('damages.toast.polygonCreateSuccess'));
			await invalidateAll();
			onCreateSuccess?.(newItem);
		},
		onError: (error) => toastApiError(error),
	});

	// --- UPDATE (Geometry) ---
	const updateMutation = useMutation({
		mutationFn: ({ id, data }: { id: string; data: Partial<DamagePolygonType> }) =>
			updateDamagePolygon(id, data),
		onSuccess: async (updatedItem) => {
			// We don't toast here by default to allow Link/Unlink to handle their own toasts
			// or you can add a generic toast if data.polygon exists
			await invalidateAll();
			onUpdateSuccess?.(updatedItem);
		},
		onError: (error) => toastApiError(error),
	});

	// --- DELETE ---
	const deleteMutation = useMutation({
		mutationFn: (id: string) => deleteDamagePolygon(id),
		onSuccess: async () => {
			toast.success(t('damages.toast.polygonDeleteSuccess'));
			await invalidateAll();
			onDeleteSuccess?.();
		},
		onError: (error) => toastApiError(error),
	});

	const linkPolygon = (polygonId: string, damageId: string) => {
		const toastId = toast.loading(t('damages.toast.linkingPolygon'));
		updateMutation.mutate(
			{
				id: polygonId,
				data: { damageId: damageId },
			},
			{
				onSuccess: async () => {
					await invalidateAll();

					toast.dismiss(toastId);
					toast.success(t('damages.toast.polygonLinkSuccess'));
					onLinkSuccess?.();
				},
				onError: (error) => {
					toast.dismiss(toastId);
					toastApiError(error);
				},
			}
		);
	};

	// --- UNLINK HELPER ---
	const unlinkPolygon = (polygonId: string) => {
		const toastId = toast.loading(t('damages.toast.unlinkingPolygon'));
		updateMutation.mutate(
			{
				id: polygonId,
				data: { damageId: null },
			},
			{
				onSuccess: async () => {
					await invalidateAll();
					toast.dismiss(toastId);
					toast.success(t('damages.toast.polygonUnlinkSuccess'));
					onUnlinkSuccess?.();
				},
				onError: (error) => {
					toast.dismiss(toastId);
					toastApiError(error);
				},
			}
		);
	};

	return {
		createMutation,
		updateMutation,
		deleteMutation,
		linkPolygon,
		unlinkPolygon,
		isSubmitting: createMutation.isPending || updateMutation.isPending || deleteMutation.isPending,
	};
}
